import jsPDF from "jspdf";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";

// ===== TYPES =====
interface Report {
  report_title?: string;
  status_text?: string;
  report_date: string;
  submitted_at?: string;
  is_draft?: boolean;
  is_submitted_late?: boolean;
  attachments_count?: number;
  report_content?: string;
  frequency_type?: string;
  report_type_id?: number;
  weekly_interval?: number;
  monthly_interval?: number;
}

interface PageConfig {
  width: number;
  height: number;
  margin: number;
  footerHeight: number;
  headerHeight: number;
}

interface ParsedElement {
  type: string;
  content?: string;
  level?: number;
  items?: string[];
  data?: string[][];
}

interface RenderContext {
  pdf: jsPDF;
  y: number;
  config: PageConfig;
  contentWidth: number;
}

// ===== CONFIGURATION =====
const PAGE_CONFIG: PageConfig = {
  width: 210,
  height: 297,
  margin: 10,
  footerHeight: 15,
  headerHeight: 40,
};

const COLORS = {
  primary: "#4f46e5",
  primaryLight: "#a5b4fc",
  white: "#ffffff",
  success: "#059669",
  info: "#2563eb",
  danger: "#dc2626",
  warning: "#f59e0b",
  textPrimary: "#111827",
  textSecondary: "#374151",
  textMuted: "#6b7280",
  background: "#f8fafc",
  backgroundAlt: "#f1f5f9",
  border: "#e2e8f0",
  borderDark: "#cbd5e1",
  tableHeaderBg: "#1f2937",
  codeBackground: "#f1f5f9",
  codeBorder: "#e2e8f0",
  codeText: "#7c3aed",
};

const TYPOGRAPHY = {
  headingSizes: [18, 16, 14, 12, 11, 10],
  headingColors: [
    COLORS.textPrimary,
    "#1f2937",
    COLORS.textSecondary,
    "#4b5563",
    COLORS.textMuted,
    "#9ca3af",
  ],
  baseSize: 10,
  smallSize: 9,
  tinySize: 8,
  largeSize: 12,
  titleSize: 16,
};

const LINE_HEIGHTS = {
  text: 0.4,
  table: 4,
  list: 4.5,
  code: 8,
  heading: 0.45,
};

const SPACING = {
  afterHeading: 3,
  afterParagraph: 2.5,
  afterList: 3,
  afterTable: 5,
  afterSeparator: 6,
  lineBreak: 3,
  listItemGap: 1.5,
  tableRowPadding: 2,
  minBottomMargin: 20,
};

// ===== UTILITY FUNCTIONS =====
export const formatCount = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const getFrequencyText = (report: Report): string => {
  if (!report.frequency_type || report.report_type_id === -1) return "Custom";

  const frequencyMap: Record<string, string> = {
    daily: "Daily",
    weekly:
      report.weekly_interval === 1
        ? "Weekly"
        : `Every ${report.weekly_interval} weeks`,
    monthly:
      report.monthly_interval === 1
        ? "Monthly"
        : `Every ${report.monthly_interval} months`,
  };

  return (
    frequencyMap[report.frequency_type] ||
    report.frequency_type.charAt(0).toUpperCase() +
      report.frequency_type.slice(1)
  );
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
};

const removeEmojis = (text: string): string => {
  const emojiPatterns = [
    /[\uD83D][\uDE00-\uDE4F]/g,
    /[\uD83C][\uDF00-\uDFFF]/g,
    /[\uD83D][\uDC00-\uDDFF]/g,
    /[\uD83D][\uDE80-\uDEFF]/g,
    /[\uD83E][\uDD00-\uDDFF]/g,
    /[\uD83E][\uDE00-\uDEFF]/g,
    /[\uD83C][\uDDE6-\uDDFF]/g,
    /[\u2600-\u26FF]/g,
    /[\u2700-\u27BF]/g,
    /[\uFE0F\u200D]/g,
    /[\u2194-\u2199\u21A9-\u21AA]/g,
    /[\u2B05-\u2B07\u2B1B-\u2B1C\u2B50\u2B55]/g,
    /[\u3030\u303D\u3297\u3299]/g,
  ];

  let result = text;
  emojiPatterns.forEach((pattern) => {
    result = result.replace(pattern, "");
  });

  return result.replace(/\s+/g, " ").trim();
};

const sanitizeText = (text: string | null | undefined): string => {
  if (!text) return "";

  let sanitized = removeEmojis(text.toString());

  const charMap: Record<string, string> = {
    À: "A",
    Á: "A",
    Â: "A",
    Ã: "A",
    Ä: "Ae",
    Å: "A",
    à: "a",
    á: "a",
    â: "a",
    ã: "a",
    ä: "ae",
    å: "a",
    È: "E",
    É: "E",
    Ê: "E",
    Ë: "E",
    è: "e",
    é: "e",
    ê: "e",
    ë: "e",
    Ì: "I",
    Í: "I",
    Î: "I",
    Ï: "I",
    ì: "i",
    í: "i",
    î: "i",
    ï: "i",
    Ò: "O",
    Ó: "O",
    Ô: "O",
    Õ: "O",
    Ö: "Oe",
    Ø: "O",
    ò: "o",
    ó: "o",
    ô: "o",
    õ: "o",
    ö: "oe",
    ø: "o",
    Ù: "U",
    Ú: "U",
    Û: "U",
    Ü: "Ue",
    ù: "u",
    ú: "u",
    û: "u",
    ü: "ue",
    Ñ: "N",
    ñ: "n",
    Ç: "C",
    ç: "c",
    "…": "...",
    "€": "EUR",
    "£": "GBP",
    "¥": "JPY",
  };

  // eslint-disable-next-line no-control-regex
  sanitized = sanitized.replace(/[^\x00-\x7F]/g, (char) => charMap[char] || "");
  return sanitized.replace(/\s+/g, " ").trim();
};

const parseHTMLContent = (html: string): ParsedElement[] => {
  if (!html) return [];

  const elements: ParsedElement[] = [];

  try {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    parseDOM(tempDiv, elements);
  } catch (error) {
    elements.push({ type: "paragraph", content: "Error parsing content" });
  }

  return elements;
};

const parseDOM = (container: any, elements: ParsedElement[]): void => {
  const processNode = (node: any): void => {
    if (node.nodeType === 3) {
      const text = sanitizeText(node.textContent);
      if (text) {
        elements.push({ type: "text", content: text });
      }
      return;
    }

    if (node.nodeType !== 1) return;

    const element = node as Element;
    const tagName = element.tagName.toLowerCase();

    switch (tagName) {
      case "h1":
      case "h2":
      case "h3":
      case "h4":
      case "h5":
      case "h6":
        const content = sanitizeText(element.textContent);
        if (content) {
          elements.push({
            type: "heading",
            level: parseInt(tagName.charAt(1)),
            content,
          });
        }
        break;

      case "p":
        const pContent = sanitizeText(element.textContent);
        if (pContent) {
          elements.push({ type: "paragraph", content: pContent });
        }
        break;

      case "br":
        elements.push({ type: "linebreak" });
        break;

      case "hr":
        elements.push({ type: "separator" });
        break;

      case "strong":
      case "b":
        const strongContent = sanitizeText(element.textContent);
        if (strongContent) {
          elements.push({ type: "bold", content: strongContent });
        }
        break;

      case "em":
      case "i":
        const emContent = sanitizeText(element.textContent);
        if (emContent) {
          elements.push({ type: "italic", content: emContent });
        }
        break;

      case "ul":
      case "ol":
        const listItems: string[] = [];
        element.querySelectorAll("li").forEach((li) => {
          const text = sanitizeText(li.textContent);
          if (text) listItems.push(text);
        });
        if (listItems.length > 0) {
          elements.push({
            type: tagName === "ul" ? "bulletlist" : "numberlist",
            items: listItems,
          });
        }
        break;

      case "table":
        const tableData: string[][] = [];
        element.querySelectorAll("tr").forEach((tr) => {
          const rowData: string[] = [];
          tr.querySelectorAll("td, th").forEach((cell) => {
            rowData.push(sanitizeText(cell.textContent));
          });
          if (rowData.length > 0) tableData.push(rowData);
        });
        if (tableData.length > 0) {
          elements.push({ type: "table", data: tableData });
        }
        break;

      case "code":
        const codeContent = sanitizeText(element.textContent);
        if (codeContent) {
          elements.push({ type: "code", content: codeContent });
        }
        break;

      default:
        element.childNodes.forEach(processNode);
        break;
    }
  };

  container.childNodes.forEach(processNode);
};

// ===== CONTEXT-BASED HELPERS =====
const setTextColor = (ctx: RenderContext, color: string): void => {
  const rgb = hexToRgb(color);
  ctx.pdf.setTextColor(rgb.r, rgb.g, rgb.b);
};

const setFillColor = (ctx: RenderContext, color: string): void => {
  const rgb = hexToRgb(color);
  ctx.pdf.setFillColor(rgb.r, rgb.g, rgb.b);
};

const setDrawColor = (ctx: RenderContext, color: string): void => {
  const rgb = hexToRgb(color);
  ctx.pdf.setDrawColor(rgb.r, rgb.g, rgb.b);
};

const validateContext = (ctx: RenderContext): void => {
  if (Number.isNaN(ctx.y) || ctx.y < 0) {
    throw new Error(
      `PDF layout corrupted: Invalid yPosition = ${ctx.y}. This indicates a rendering bug.`,
    );
  }
  if (ctx.y > ctx.config.height + 50) {
    // console.warn(
    //   `yPosition (${ctx.y}) exceeds page height. Possible pagination issue.`,
    // );
  }
};

const addNewPageIfNeeded = (
  ctx: RenderContext,
  requiredHeight: number,
): RenderContext => {
  validateContext(ctx);

  const availableSpace =
    ctx.config.height - ctx.y - ctx.config.footerHeight - ctx.config.margin;

  if (requiredHeight > availableSpace) {
    ctx.pdf.addPage();
    return { ...ctx, y: ctx.config.margin };
  }
  return ctx;
};

const addText = (
  ctx: RenderContext,
  text: string,
  fontSize: number,
  fontStyle: "normal" | "bold" | "italic" = "normal",
  color: string = COLORS.textPrimary,
  maxWidth?: number,
): RenderContext => {
  const sanitized = sanitizeText(text);
  if (!sanitized) return ctx;

  ctx.pdf.setFontSize(fontSize);
  ctx.pdf.setFont("helvetica", fontStyle);
  setTextColor(ctx, color);

  const width = maxWidth || ctx.contentWidth;
  const lines = ctx.pdf.splitTextToSize(sanitized, width);
  const lineHeight = fontSize * LINE_HEIGHTS.text;

  let updatedCtx = addNewPageIfNeeded(ctx, lines.length * lineHeight + 5);

  lines.forEach((line: string) => {
    updatedCtx.pdf.text(line, ctx.config.margin, updatedCtx.y);
    updatedCtx = { ...updatedCtx, y: updatedCtx.y + lineHeight };
  });

  return updatedCtx;
};

const renderTableHeader = (
  ctx: RenderContext,
  headerRow: string[],
  colWidth: number,
): RenderContext => {
  const headerHeight = 10;

  headerRow.forEach((cell, colIndex) => {
    const sanitized = sanitizeText(cell);
    const x = ctx.config.margin + colIndex * colWidth;

    setFillColor(ctx, COLORS.tableHeaderBg);
    ctx.pdf.rect(x, ctx.y, colWidth, headerHeight, "F");

    setDrawColor(ctx, COLORS.border);
    ctx.pdf.setLineWidth(0.3);
    ctx.pdf.rect(x, ctx.y, colWidth, headerHeight, "S");

    ctx.pdf.setFont("helvetica", "bold");
    ctx.pdf.setFontSize(TYPOGRAPHY.smallSize);
    setTextColor(ctx, COLORS.white);

    const lines = ctx.pdf.splitTextToSize(sanitized, colWidth - 4);
    const textY = ctx.y + headerHeight / 2 + 1;

    if (lines[0]) {
      ctx.pdf.text(lines[0], x + 2, textY);
    }
  });

  return { ...ctx, y: ctx.y + headerHeight };
};

// ===== MAIN EXPORT FUNCTION =====
export const exportReportToPDF = async (report: any): Promise<void> => {
  try {
    if (!report) {
      throw new Error("Report object is required");
    }

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const contentWidth = PAGE_CONFIG.width - PAGE_CONFIG.margin * 2;

    let ctx: RenderContext = {
      pdf,
      y: PAGE_CONFIG.margin,
      config: PAGE_CONFIG,
      contentWidth,
    };

    // ===== RENDER HEADER =====
    setFillColor(ctx, COLORS.primary);
    pdf.rect(0, 0, PAGE_CONFIG.width, PAGE_CONFIG.headerHeight, "F");

    pdf.setFontSize(TYPOGRAPHY.titleSize);
    pdf.setFont("helvetica", "bold");
    setTextColor(ctx, COLORS.white);

    const title = sanitizeText(report.report_title || "Untitled Report");
    const titleLines = pdf.splitTextToSize(title, contentWidth - 20);

    titleLines.forEach((line: string, index: number) => {
      pdf.text(line, PAGE_CONFIG.margin, 14 + index * 6);
    });

    // Status badges
    const badges = [
      { text: sanitizeText(report.status_text || ""), bg: COLORS.success },
      { text: sanitizeText(getFrequencyText(report)), bg: COLORS.info },
    ];

    if (report.is_submitted_late) {
      badges.push({ text: "Late", bg: COLORS.danger });
    }

    let badgeX = PAGE_CONFIG.margin;
    const badgeY = 22;
    const badgeHeight = 6;
    const badgePadding = 3;

    badges.forEach((badge) => {
      if (!badge.text) return;

      pdf.setFontSize(TYPOGRAPHY.tinySize);
      pdf.setFont("helvetica", "bold");

      const textWidth = pdf.getTextWidth(badge.text);
      const badgeWidth = textWidth + badgePadding * 2;

      setFillColor(ctx, badge.bg);
      pdf.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, 2, 2, "F");

      setTextColor(ctx, COLORS.white);
      pdf.text(badge.text, badgeX + badgePadding, badgeY + 4.5);

      badgeX += badgeWidth + 3;
    });

    // Metadata
    pdf.setFontSize(TYPOGRAPHY.smallSize);
    pdf.setFont("helvetica", "normal");
    setTextColor(ctx, "#cbd5e1");

    const reportDate = humanReadableDate(report.report_date);
    const submittedDate = report.is_draft
      ? ""
      : ` • Submitted: ${humanReadableDate(report?.submitted_at, true)}`;
    const attachments =
      report.attachments_count && report.attachments_count > 0
        ? ` • ${report.attachments_count} attachment${report.attachments_count > 1 ? "s" : ""}`
        : "";

    const metaText = `Report Date: ${reportDate}${submittedDate}${attachments}`;
    pdf.text(metaText, PAGE_CONFIG.margin, PAGE_CONFIG.headerHeight - 6);

    ctx.y = PAGE_CONFIG.headerHeight + 8;

    // ===== RENDER CONTENT =====
    const elements = parseHTMLContent(report.report_content || "");

    if (elements.length === 0) {
      ctx = addText(
        ctx,
        "No content available",
        TYPOGRAPHY.baseSize,
        "italic",
        COLORS.textMuted,
      );
    }

    for (const element of elements) {
      validateContext(ctx);

      switch (element.type) {
        case "heading":
          ctx.y += SPACING.afterHeading;
          const level = Math.min(Math.max((element.level || 1) - 1, 0), 5);
          ctx = addText(
            ctx,
            element.content || "",
            TYPOGRAPHY.headingSizes[level],
            "bold",
            TYPOGRAPHY.headingColors[level],
          );
          ctx.y += SPACING.afterHeading;
          break;

        case "paragraph":
          ctx = addText(
            ctx,
            element.content || "",
            TYPOGRAPHY.baseSize,
            "normal",
            COLORS.textSecondary,
          );
          ctx.y += SPACING.afterParagraph;
          break;

        case "bold":
          ctx = addText(
            ctx,
            element.content || "",
            TYPOGRAPHY.baseSize,
            "bold",
            COLORS.textPrimary,
          );
          ctx.y += 1;
          break;

        case "italic":
          ctx = addText(
            ctx,
            element.content || "",
            TYPOGRAPHY.baseSize,
            "italic",
            COLORS.textMuted,
          );
          ctx.y += 1;
          break;

        case "code":
          const sanitizedCode = sanitizeText(element.content || "");
          if (sanitizedCode) {
            ctx = addNewPageIfNeeded(ctx, LINE_HEIGHTS.code);

            const codeWidth = Math.min(
              pdf.getTextWidth(sanitizedCode) + 8,
              contentWidth,
            );

            setFillColor(ctx, COLORS.codeBackground);
            setDrawColor(ctx, COLORS.codeBorder);
            pdf.setLineWidth(0.3);
            pdf.roundedRect(
              PAGE_CONFIG.margin,
              ctx.y - 1,
              codeWidth,
              LINE_HEIGHTS.code - 2,
              1,
              1,
              "FD",
            );

            pdf.setFontSize(TYPOGRAPHY.smallSize);
            pdf.setFont("courier", "normal");
            setTextColor(ctx, COLORS.codeText);
            pdf.text(sanitizedCode, PAGE_CONFIG.margin + 4, ctx.y + 3);

            ctx.y += LINE_HEIGHTS.code;
          }
          break;

        case "bulletlist":
          // eslint-disable-next-line no-loop-func
          for (const item of element.items || []) {
            const sanitized = sanitizeText(item);
            if (!sanitized) continue;

            pdf.setFontSize(TYPOGRAPHY.baseSize);
            pdf.setFont("helvetica", "normal");

            const lines = pdf.splitTextToSize(sanitized, contentWidth - 10);
            const itemHeight =
              lines.length * LINE_HEIGHTS.list + SPACING.listItemGap;

            ctx = addNewPageIfNeeded(ctx, itemHeight);

            setTextColor(ctx, COLORS.info);
            pdf.setFont("helvetica", "bold");
            pdf.text("•", PAGE_CONFIG.margin + 2, ctx.y);

            setTextColor(ctx, COLORS.textSecondary);
            pdf.setFont("helvetica", "normal");

            lines.forEach((line: string, lineIndex: number) => {
              pdf.text(
                line,
                PAGE_CONFIG.margin + 7,
                ctx.y + lineIndex * LINE_HEIGHTS.list,
              );
            });

            ctx.y += itemHeight;
          }
          ctx.y += SPACING.afterList;
          break;

        case "numberlist":
          // eslint-disable-next-line no-loop-func
          for (let index = 0; index < (element.items || []).length; index++) {
            const item = element.items![index];
            const sanitized = sanitizeText(item);
            if (!sanitized) continue;

            pdf.setFontSize(TYPOGRAPHY.baseSize);
            const lines = pdf.splitTextToSize(sanitized, contentWidth - 12);
            const itemHeight =
              lines.length * LINE_HEIGHTS.list + SPACING.listItemGap;

            ctx = addNewPageIfNeeded(ctx, itemHeight);

            pdf.setFont("helvetica", "bold");
            setTextColor(ctx, COLORS.info);
            pdf.text(`${index + 1}.`, PAGE_CONFIG.margin + 2, ctx.y);

            pdf.setFont("helvetica", "normal");
            setTextColor(ctx, COLORS.textSecondary);

            lines.forEach((line: string, lineIndex: number) => {
              pdf.text(
                line,
                PAGE_CONFIG.margin + 9,
                ctx.y + lineIndex * LINE_HEIGHTS.list,
              );
            });

            ctx.y += itemHeight;
          }
          ctx.y += SPACING.afterList;
          break;

        case "table":
          if (!element.data || element.data.length === 0) break;

          const tableData = element.data;
          const colCount = tableData[0].length;
          const colWidth = contentWidth / colCount;

          let isFirstRowOfTable = true;
          const headerRow = tableData[0];

          // eslint-disable-next-line no-loop-func
          for (let rowIndex = 0; rowIndex < tableData.length; rowIndex++) {
            const row = tableData[rowIndex];
            let maxLines = 1;
            pdf.setFontSize(TYPOGRAPHY.smallSize);

            row.forEach((cell: string) => {
              const sanitized = sanitizeText(cell);
              const cellLines = pdf.splitTextToSize(sanitized, colWidth - 4);
              maxLines = Math.max(maxLines, cellLines.length);
            });

            const rowHeight =
              maxLines * LINE_HEIGHTS.table + SPACING.tableRowPadding * 2;

            const prevY = ctx.y;
            ctx = addNewPageIfNeeded(ctx, rowHeight + 5);
            const didAddPage = ctx.y !== prevY && ctx.y === PAGE_CONFIG.margin;

            if (didAddPage && !isFirstRowOfTable) {
              ctx = renderTableHeader(ctx, headerRow, colWidth);
            }

            row.forEach((cell: string, colIndex: number) => {
              const sanitized = sanitizeText(cell);
              const x = PAGE_CONFIG.margin + colIndex * colWidth;
              const y = ctx.y;

              if (rowIndex === 0) {
                setFillColor(ctx, COLORS.tableHeaderBg);
                pdf.rect(x, y, colWidth, rowHeight, "F");
                pdf.setFont("helvetica", "bold");
                setTextColor(ctx, COLORS.white);
              } else {
                if (rowIndex % 2 === 1) {
                  setFillColor(ctx, COLORS.background);
                  pdf.rect(x, y, colWidth, rowHeight, "F");
                }
                pdf.setFont("helvetica", "normal");
                setTextColor(ctx, COLORS.textSecondary);
              }

              setDrawColor(ctx, COLORS.border);
              pdf.setLineWidth(0.2);
              pdf.rect(x, y, colWidth, rowHeight, "S");

              pdf.setFontSize(TYPOGRAPHY.smallSize);
              const cellLines = pdf.splitTextToSize(sanitized, colWidth - 4);

              cellLines.forEach((line: string, lineIdx: number) => {
                const textY =
                  y +
                  SPACING.tableRowPadding +
                  3.5 +
                  lineIdx * LINE_HEIGHTS.table;
                pdf.text(line, x + 2, textY);
              });
            });

            ctx.y += rowHeight;
            isFirstRowOfTable = false;
          }

          ctx.y += SPACING.afterTable;
          ctx = addNewPageIfNeeded(ctx, 15);
          break;

        case "separator":
          ctx = addNewPageIfNeeded(ctx, 8);
          setDrawColor(ctx, COLORS.primary);
          pdf.setLineWidth(0.8);
          pdf.line(
            PAGE_CONFIG.margin,
            ctx.y,
            PAGE_CONFIG.width - PAGE_CONFIG.margin,
            ctx.y,
          );
          ctx.y += SPACING.afterSeparator;
          break;

        case "linebreak":
          ctx.y += SPACING.lineBreak;
          break;

        case "text":
          if (element.content) {
            ctx = addText(
              ctx,
              element.content,
              TYPOGRAPHY.baseSize,
              "normal",
              COLORS.textSecondary,
            );
            ctx.y += 1;
          }
          break;

        default:
          break;
      }
    }

    // ===== RENDER FOOTERS =====
    const pageCount = pdf.getNumberOfPages();
    const currentYear = new Date().getFullYear();

    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);

      const footerY = PAGE_CONFIG.height - PAGE_CONFIG.footerHeight + 3;

      setDrawColor(ctx, COLORS.borderDark);
      pdf.setLineWidth(0.5);
      pdf.line(
        PAGE_CONFIG.margin,
        footerY,
        PAGE_CONFIG.width - PAGE_CONFIG.margin,
        footerY,
      );

      pdf.setFontSize(8);
      pdf.setFont("helvetica", "normal");
      setTextColor(ctx, COLORS.textMuted);
      pdf.text(
        `© ${currentYear} Lakera Global Services`,
        PAGE_CONFIG.margin,
        footerY + 6,
      );

      const pageText = `${i} / ${pageCount}`;
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "bold");

      const pageNumWidth = pdf.getTextWidth(pageText) + 8;
      const pageNumX = PAGE_CONFIG.width - PAGE_CONFIG.margin - pageNumWidth;
      const pageNumY = footerY + 2;

      setFillColor(ctx, COLORS.tableHeaderBg);
      pdf.roundedRect(pageNumX, pageNumY, pageNumWidth, 6, 1.5, 1.5, "F");

      setTextColor(ctx, COLORS.white);
      pdf.text(pageText, pageNumX + 4, pageNumY + 4.5);
    }

    // ===== SAVE PDF =====
    const sanitizedTitle = sanitizeText(report.report_title || "report")
      .replace(/[^a-z0-9\s-]/gi, "")
      .replace(/\s+/g, "_")
      .toLowerCase()
      .substring(0, 50);

    const dateStr = new Date(report.report_date).toISOString().split("T")[0];
    const filename = `${sanitizedTitle}_${dateStr}.pdf`;

    pdf.save(filename);
  } catch (error) {
    throw new Error(
      `Failed to export PDF: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
};
